import React, { useState, useContext, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Button,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  FormControl,
  Typography,
  Autocomplete,
  Box,
  Popper,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../../app/hooks';
import { selectProject } from '../../../features/project/projectSlice';
import { PriceGridContext } from '../../../context/PriceGridContext';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../features/snackbar/snackbarSlice';
import { unitStatusTitles } from '../../../utils/Constants';
import { IStatus } from '../../../types/project';
import { IUserArray } from '../../../types/user';
import { IUnit } from '../../../types/unit';
import { IRealtorInfo } from '../../../types/CreateDealForm';
import { numToCurrency, numberWithCommas, netHST, grossPriceFromNetHST, getSalesStatusCodes } from '../../../utils/Functions';
import LoadingWrapper from '../../common/LoadingWrapper';
import VirtualizedTable from '../../tables/VirtualizedTable';
import { selectUser } from '../../../features/auth/authSlice';
import { handleModal } from '../../../features/modal/modalSlice';


const DARK_GREEN = '#2e453d';
const BORDER = '#e9e9e6';
const LABEL_GREY = '#6a6a67';

// Card-style grouping for the bulk-edit fields below — purely presentational, groups related
// controls under a heading instead of one long undifferentiated row of fields.
const Section = ({
  title,
  children,
  dense,
  rowSpacing,
}: {
  title: string;
  children: React.ReactNode;
  // Tighter padding/gap — used for the Summary, which just shows read-only stat tiles rather than
  // full-size form fields.
  dense?: boolean;
  // Overrides just the vertical gap between wrapped rows of fields, independent of the horizontal spacing.
  rowSpacing?: number;
}) => (
  <Box sx={{ mb: dense ? 1.5 : 2.5, p: dense ? 1.25 : 2.5, backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '8px' }}>
    <Typography
      sx={{
        fontSize: '0.72rem',
        fontWeight: 700,
        color: LABEL_GREY,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        mb: dense ? 0.75 : 1.5,
      }}
    >
      {title}
    </Typography>
    <Grid container spacing={dense ? 1 : 2} rowSpacing={rowSpacing ?? (dense ? 1 : 2)}>
      {children}
    </Grid>
  </Box>
);

const StatTile = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Grid item md={1.5} sm={3} xs={4}>
    <Box sx={{ px: 1, py: 0.5, backgroundColor: '#f7f7f5', border: `1px solid ${BORDER}`, borderRadius: '4px', textAlign: 'center', height: '100%' }}>
      <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: LABEL_GREY, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: '#1a1a1a' }}>{value}</Typography>
    </Box>
  </Grid>
);

const EditSelected = () => {
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const storeDispatch = useAppDispatch();
  const {
    setSelectingUnits,
    setSelectedUnits,
    selectedUnits,
    units,
    setUnits,
    setEditOpen,
    draft,
    selectedPriceList,
    setModalType,
    leasing,
  } = useContext(PriceGridContext);

  const allStatus: IStatus[] = leasing
    ? [
        ...project.status.filter((status: IStatus) => status.type === 'leasing'),
        ...unitStatusTitles.filter((status: IStatus) => status.type === 'leasing'),
      ]
    : [
        ...project.status.filter((status: IStatus) => status.type === 'preconstruction'),
        ...unitStatusTitles.filter((status: IStatus) => status.type === 'preconstruction'),
      ];
  const salesStatusCodes = getSalesStatusCodes(project);

  const [displayType, setDisplayType] = useState<string[]>(
    leasing
      ? allStatus.map((status: IStatus) => status.code)
      : allStatus.filter((status: IStatus) => !salesStatusCodes.includes(status.code)).map((status: IStatus) => status.code)
  );

  const [percentages, setPercentages] = useState<any[]>([]);
  const [amountChange, setAmountChange] = useState<any[]>([]);
  const [rentalChange, setRentalChange] = useState<any[]>([]);
  const [status, setStatus] = useState<any[]>([]);
  const [changedPrice, setChangedPrice] = useState<any[]>([]);
  const [changedModel, setChangedModel] = useState<any[]>([]);
  const [changedUnitType, setChangedUnitType] = useState<any[]>([]);
  const [changedAllocation, setChangedAllocation] = useState<any[]>([]);
  const [allAmount, setAllAmount] = useState('');
  const [allPercent, setAllPercent] = useState('');
  const [allFloorPremiums, setAllFloorPremiums] = useState('');
  const [allSamePrice, setAllSamePrice] = useState<string>('0');
  const [allSqFt, setAllSqFt] = useState('');
  const [allRental, setAllRental] = useState('');
  const [allRentalSamePrice, setAllRentalSamePrice] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [allModels, setAllModels] = useState('');
  const [allUnitTypes, setAllUnitTypes] = useState('');
  const [allAllocations, setAllAllocations] = useState('');
  const [filteredSelectedUnits, setFilteredSelectedUnits] = useState<IUnit[]>([]);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [realtors, setRealtors] = useState<IRealtorInfo[]>([]);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);

  const checkSameModel = filteredSelectedUnits.every((unit: IUnit) => unit.modelType === filteredSelectedUnits[0].modelType);

  const [searchRealtors] = useLazyQuery<IUserArray>(GETREALTORS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setRealtors(data.userMany);
    },
  });

  const [updateUnits, { loading: unitLoading }] = useMutation(UPDATEUNIT, {
    onCompleted: (data) => {
      let updatedSelectedUnits = units.map((displayedUnit: any) => {
        let updatedUnit = data.unitUpdateManyInfo.find((updatedUnit: any) => displayedUnit._id === updatedUnit._id);

        if (updatedUnit) {
          return {
            ...displayedUnit,
            allocation: updatedUnit.allocation,
            allocatedDate: updatedUnit.allocatedDate,
            tempAllocation: updatedUnit.tempAllocation,
            basePrice: updatedUnit.basePrice,
            status: updatedUnit.status ? updatedUnit.status : displayedUnit.status,
            leaseStatus: updatedUnit.leaseStatus ? updatedUnit.leaseStatus : displayedUnit.leaseStatus,
            modelType: updatedUnit.modelType,
            unitType: updatedUnit.unitType,
            size: updatedUnit.size,
            exposure: updatedUnit.exposure,
            bathroom: updatedUnit.bathroom,
            outdoor: updatedUnit.outdoor,
            rental: updatedUnit.rental,
          };
        } else return displayedUnit;
      });

      setEditOpen(false);
      setSelectingUnits(false);
      setSelectedUnits([]);
      setFilteredSelectedUnits([]);
      setUnits(updatedSelectedUnits);
      setOpenConfirm(false);
      setModalType('');
      storeDispatch(handleModal(false));
      storeDispatch(showSuccessSnackbar('Units Successfully Updated'));
    },
    onError: (err) => {
      storeDispatch(showErrorSnackbar('There is an error updating units'));
      setOpenConfirm(false);
      console.log(err, 'err');
    },
  });

  const [updatePriceList] = useMutation(UPDATEPRICELIST, {
    onCompleted: (data) => {
      let updatedSelectedUnits = units.map((displayedUnit: any) => {
        let updatedUnit = data.priceListUpdateById.record.draft.find((updatedUnit: any) => displayedUnit._id === updatedUnit._id);

        if (updatedUnit) {
          return {
            ...displayedUnit,
            basePrice: updatedUnit.basePrice,
            status: updatedUnit.status,
            modelType: updatedUnit.modelType,
            unitType: updatedUnit.unitType,
            size: updatedUnit.size,
            exposure: updatedUnit.exposure,
            bathroom: updatedUnit.bathroom,
            outdoor: updatedUnit.outdoor,
          };
        } else return displayedUnit;
      });

      setEditOpen(false);
      setSelectingUnits(false);
      setSelectedUnits([]);
      setFilteredSelectedUnits([]);
      setUnits(updatedSelectedUnits);
      setOpenConfirm(false);
      storeDispatch(showSuccessSnackbar('Units Successfully Updated'));
    },
  });

  const setData = (data: IUnit[]) => {
    setPercentages(data.map((unit: IUnit) => 0));
    setAmountChange(data.map((unit: IUnit) => 0));
    setRentalChange(data.map((unit: IUnit) => (unit.rental ? unit.rental : 0)));
    setStatus(leasing ? data.map((unit: IUnit) => unit.leaseStatus) : data.map((unit: IUnit) => unit.status));
    setChangedPrice(data.map((unit: IUnit) => unit.basePrice));
    setChangedModel(data.map((unit: IUnit) => unit.modelType));
    setChangedUnitType(data.map((unit: IUnit) => unit.unitType));
    setChangedAllocation(data.map((unit: IUnit) => unit));
    setFilteredSelectedUnits(data);
  };

  useEffect(() => {
    if (draft) {
      let filteredData = units.filter((unit: IUnit) => selectedUnits.some((selectedUnit: IUnit) => selectedUnit._id === unit._id));
      setData(filteredData);
    } else {
      setData(selectedUnits.filter((unit: IUnit) => displayType.includes(leasing ? unit.leaseStatus! : unit.status)));
    }
  }, [displayType, selectedUnits]);

  let editableKeyToFocus: any = useRef(null);

  const CustomPopper = function (props: any) {
    return <Popper open={true} {...props} style={{ width: 250 }} placement="bottom-start" />;
  };

  const unitColumns = useMemo(() => {
    let array = [
      {
        Header: 'Name',
        width: 90,
        accessor: (rowData: IUnit) => rowData.suite,
      },
      {
        Header: 'Status',
        width: 90,
        accessor: (rowData: IUnit) => {
          return <strong>{leasing ? rowData.leaseStatus : rowData.status}</strong>;
        },
      },
      {
        Header: 'New Status',
        width: 130,
        accessor: (rowData: IUnit, index: number) => {
          return (
            <FormControl sx={{ width: '100%' }}>
              <InputLabel id="status-label">Status</InputLabel>
              <Select
                sx={{ width: '100%' }}
                label={'Status'}
                title="Status"
                size="small"
                labelId="status-label"
                id="status-select"
                value={status ? status[index] : ''}
                onChange={(e: any) => {
                  editableKeyToFocus.current = null;
                  handleDropdownChange(e, index);
                }}
              >
                {allStatus.map((status, index) => {
                  return (
                    <MenuItem key={index} value={status.code}>
                      {status.code}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          );
        },
      },
      {
        Header: 'Size',
        width: 70,
        accessor: (rowData: IUnit) => {
          return <strong>{rowData.size}</strong>;
        },
      },
    ];

    if (!leasing) {
      let originalPrice = {
        Header: 'Original Price',
        width: 110,
        accessor: (rowData: IUnit) => {
          return numToCurrency.format(rowData.basePrice);
        },
      };
      let originalNetPrice = {
        Header: 'Original Net Price',
        width: 110,
        accessor: (rowData: IUnit) => {
          return numToCurrency.format(netHST(rowData.basePrice, project));
        },
      };
      let newPrice = {
        Header: 'New Price',
        width: 130,
        accessor: (rowData: IUnit, index: number) => {
          const key = `price${index}`;
          return (
            <TextField
              size="small"
              fullWidth
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              label={'New Price'}
              title={'New Price'}
              onBlur={() => (editableKeyToFocus.current = null)}
              onChange={(e: any) => {
                editableKeyToFocus.current = key;
                handleNewPrice(e, index, rowData.basePrice);
                setSelectionStart(e.target.selectionStart);
                setSelectionEnd(e.target.selectionEnd);
              }}
              value={changedPrice[index]}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              placeholder="Enter Price"
              required={true}
              onFocus={(e) => {
                e.target.selectionStart = selectionStart;
                e.target.selectionEnd = selectionEnd;
              }}
              autoFocus={key === editableKeyToFocus.current}
            />
          );
        },
      };
      let netPrice = {
        Header: 'Net Price',
        width: 130,
        accessor: (rowData: IUnit, index: number) => {
          const key = `netPrice${index}`;
          const displayValue = changedPrice[index] != null ? Math.round(netHST(changedPrice[index], project)) : '';
          return (
            <TextField
              size="small"
              fullWidth
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              label={'Net Price'}
              title={'Net Price'}
              onBlur={() => (editableKeyToFocus.current = null)}
              onChange={(e: any) => {
                editableKeyToFocus.current = key;
                handleNewNetPrice(e, index, rowData.basePrice);
                setSelectionStart(e.target.selectionStart);
                setSelectionEnd(e.target.selectionEnd);
              }}
              value={displayValue}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              placeholder="Enter Net Price"
              onFocus={(e) => {
                e.target.selectionStart = selectionStart;
                e.target.selectionEnd = selectionEnd;
              }}
              autoFocus={key === editableKeyToFocus.current}
            />
          );
        },
      };
      let pricePercentage = {
        Header: 'Increase Price Percentage',
        width: 110,
        accessor: (rowData: IUnit, index: number) => {
          const key = `percentage${index}`;
          return (
            <TextField
              size="small"
              fullWidth
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              label={'Percentage'}
              title={'Percentage'}
              onBlur={() => (editableKeyToFocus.current = null)}
              onChange={(e: any) => {
                editableKeyToFocus.current = key;
                setSelectionStart(e.target.selectionStart);
                setSelectionEnd(e.target.selectionEnd);
                handlePercent(e, index, rowData.basePrice);
              }}
              value={percentages[index]}
              InputProps={{
                startAdornment: <InputAdornment position="start">%</InputAdornment>,
              }}
              placeholder="Enter Percentage"
              onFocus={(e) => {
                e.target.selectionStart = selectionStart;
                e.target.selectionEnd = selectionEnd;
              }}
              autoFocus={key === editableKeyToFocus.current}
            />
          );
        },
      };
      let priceAmount = {
        Header: 'Increase Price Amount',
        width: 110,
        accessor: (rowData: IUnit, index: number) => {
          const key = `amount${index}`;
          return (
            <TextField
              size="small"
              fullWidth
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              label={'Amount'}
              title={'Amount'}
              type="number"
              onBlur={() => (editableKeyToFocus.current = null)}
              onChange={(e: any) => {
                editableKeyToFocus.current = key;
                setSelectionStart(e.target.selectionStart);
                setSelectionEnd(e.target.selectionEnd);
                handleAmountInput(e, index, rowData.basePrice);
              }}
              value={amountChange[index]}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onFocus={(e) => {
                e.target.selectionStart = selectionStart;
                e.target.selectionEnd = selectionEnd;
              }}
              autoFocus={key === editableKeyToFocus.current}
              placeholder="Enter Amount"
            />
          );
        },
      };
      let allocation = {
        Header: 'Allocation',
        width: 160,
        accessor: (rowData: IUnit, index: number) => {
          const key = `allocation${index}`;
          return (
            <Autocomplete
              sx={{
                width: '100%',
                '& .MuiFormLabel-asterisk': {
                  color: 'red',
                },
              }}
              id={'search'}
              disableClearable
              freeSolo={true}
              options={realtors.map((realtor: any) => realtor.fullName)}
              getOptionLabel={(option: any) => option.fullName || option}
              onBlur={() => (editableKeyToFocus.current = null)}
              // getOptionSelected={(option, value) => {
              //   return option.fullName === value;
              // }}
              openOnFocus={true}
              PopperComponent={CustomPopper}
              value={
                changedAllocation[index] && changedAllocation[index].allocation
                  ? changedAllocation[index].allocation.fullName
                  : changedAllocation[index].tempAllocation
                  ? `${changedAllocation[index].tempAllocation}`
                  : ''
              }
              onChange={handleSearchInput(index)}
              onInputChange={(e: any) => {
                if (e) {
                  editableKeyToFocus.current = key;
                }
                handleInputChange(e, index);
              }}
              renderInput={(params) => (
                <TextField
                  required={false}
                  {...params}
                  size="small"
                  label={'Search By Name'}
                  autoFocus={key === editableKeyToFocus.current}
                />
              )}
            />
          );
        },
      };

      let originalPpsf = {
        Header: 'Original PPSF',
        width: 100,
        accessor: (rowData: IUnit) => {
          return <strong>{numToCurrency.format(rowData.basePrice / rowData.size)}</strong>;
        },
      };
      let newPpsf = {
        Header: 'New PPSF',
        width: 100,
        accessor: (rowData: IUnit, index: number) => {
          return <strong>{numToCurrency.format(changedPrice[index] / rowData.size)}</strong>;
        },
      };
      let priceColumns = [originalPrice, originalNetPrice, newPrice, netPrice, pricePercentage, priceAmount, allocation, originalPpsf, newPpsf]
      array.splice(1, 0, ...priceColumns);
    } else {
      let unitType = {
        Header: 'Unit Type',
        width: 90,
        accessor: (rowData: IUnit, index: number) => {
          return <strong>{rowData.unitType}</strong>;
        },
      };
      let modelType = {
        Header: 'Model Type',
        width: 90,
        accessor: (rowData: IUnit, index: number) => {
          return <strong>{rowData.modelType ? rowData.modelType : ''}</strong>;
        },
      };
      let bathrooms = {
        Header: 'Bathrooms',
        width: 80,
        accessor: (rowData: IUnit, index: number) => {
          return <strong>{rowData.bathroom ? rowData.bathroom : ''}</strong>;
        },
      };
      array.splice(3, 0, unitType, modelType, bathrooms);
    }

    if (filteredSelectedUnits.length && filteredSelectedUnits[0].rental !== null) {
      let rental = {
        Header: 'Rental',
        width: 130,
        accessor: (rowData: IUnit, index: number) => {
          const key = `rental${index}`;
          return (
            <TextField
              size="small"
              fullWidth
              required
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              label={'Rental'}
              title={'Rental'}
              onBlur={() => (editableKeyToFocus.current = null)}
              onChange={(e: any) => {
                editableKeyToFocus.current = key;
                setSelectionStart(e.target.selectionStart);
                setSelectionEnd(e.target.selectionEnd);
                handleRentalInput(e, index, rowData.rental!);
              }}
              value={rentalChange[index]}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              onFocus={(e) => {
                e.target.selectionStart = selectionStart;
                e.target.selectionEnd = selectionEnd;
              }}
              autoFocus={key === editableKeyToFocus.current}
              placeholder="Enter Rental Amount"
            />
          );
        },
      };
      array.splice(6, 0, rental);
    }

    return array;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changedPrice, status, filteredSelectedUnits, rentalChange, changedAllocation, project]);

  const handleDisplayType = (type: string) => {
    if (type === 'firm') {
      setDisplayType(salesStatusCodes.filter((code: string) => code !== 'C'));
    } else if (type === 'conditional') {
      setDisplayType(['C']);
    } else
      setDisplayType(allStatus.filter((status: IStatus) => !salesStatusCodes.includes(status.code)).map((status: IStatus) => status.code));
  };

  const handleDropdownChange = (event: React.ChangeEvent<{ value: unknown }>, index: number) => {
    const newStatus = status.map((status: any, i: number) => {
      if (index === i) {
        return event.target.value as string;
      } else {
        return status;
      }
    });
    setStatus(newStatus);
  };

  const handleSearchInput = (index: number) => (e: any, values: any) => {
    let selectedRealtor = realtors.find((realtor: any) => realtor.fullName === values);
    if (selectedRealtor) {
      const newAllocation = filteredSelectedUnits.map((unit, i) => {
        if (index === i) {
          return {
            ...unit,
            allocation: {
              _id: selectedRealtor?.realtor._id,
              fullName: selectedRealtor?.fullName,
            },
            tempAllocation: null,
          };
        } else if (changedAllocation[i]) {
          return {
            ...unit,
            tempAllocation: !changedAllocation[i].allocation ? changedAllocation[i].tempAllocation : '',
            allocation: changedAllocation[i].allocation ? changedAllocation[i].allocation : null,
          };
        } else return unit;
      });

      setChangedAllocation(newAllocation as any);
    }
  };

  const handleInputChange = (event: any, index: number) => {
    if (event && (event.target.value || event.target.value === '')) {
      searchRealtors({ variables: { filter: { type: 'Realtor', locked: false, search: event.target.value } } });
      const newAllocation = filteredSelectedUnits.map((unit, i) => {
        if (index === i) {
          return {
            ...unit,
            tempAllocation: event.target.value,
            allocation: null,
          };
        } else if (changedAllocation[i]) {
          return {
            ...unit,
            tempAllocation: !changedAllocation[i].allocation ? changedAllocation[i].tempAllocation : '',
            allocation: changedAllocation[i].allocation ? changedAllocation[i].allocation : null,
          };
        } else return unit;
      });
      setChangedAllocation(newAllocation as any);
    }
  };

  const handleAllAllocations = () => (e: any, values: any) => {
    let selectedRealtor = realtors.find((realtor: any) => realtor.fullName === values);
    if (selectedRealtor) {
      const newAllocation = filteredSelectedUnits.map((unit, i) => {
        return {
          ...unit,
          allocation: {
            _id: selectedRealtor?.realtor._id,
            fullName: selectedRealtor?.fullName,
          },
          tempAllocation: null,
        };
      });
      setChangedAllocation(newAllocation as any);
    }
  };

  const handleAllAllocationsChange = (event: any) => {
    if (event) {
      searchRealtors({ variables: { filter: { type: 'Realtor', locked: false, search: event.target.value } } });
      const newAllocation = filteredSelectedUnits.map((unit, i) => {
        return {
          ...unit,
          tempAllocation: event.target.value,
          allocation: null,
        };
      });
      setChangedAllocation(newAllocation as any);
    }
  };

  const submitChanges = (e: any) => {
    e.preventDefault();
    if (draft && selectedPriceList) {
      let updatedUnits = filteredSelectedUnits.map((updatedUnit: any, index: number) => {
        return {
          ...updatedUnit,
          basePrice: changedPrice[index],
          status: status[index],
          modelType: updatedUnit.modelType,
          unitType: updatedUnit.unitType,
          size: parseFloat(updatedUnit.size),
          exposure: updatedUnit.exposure,
          bathroom: parseFloat(updatedUnit.bathroom),
          allocation: updatedUnit.allocation,
          tempAllocation: updatedUnit.tempAllocation,
        };
      });

      let allUnits = units.map((unit: IUnit) => {
        let selectedUnit = updatedUnits.find((updatedUnit: IUnit) => updatedUnit.suite === unit.suite);

        if (selectedUnit) {
          return selectedUnit;
        } else return unit;
      });
      updatePriceList({ variables: { _id: selectedPriceList._id, record: { draft: allUnits } } });
    } else {
      let updatedUnits = filteredSelectedUnits.map((updatedUnit: any, index: number) => {
        let history: any = {
          type: '',
          description: '',
          timestamp: new Date(),
          user: user._id,
        };
        let historyDescription = [];
        let historyType = [];
        let allocatedDate = updatedUnit.allocatedDate;
        let removeAllocations = false;

        if (changedPrice[index] !== filteredSelectedUnits[index].basePrice) {
          historyType.push('Price');
          historyDescription.push(
            `Price has been changed to from ${numToCurrency.format(updatedUnit.basePrice)} to ${numToCurrency.format(changedPrice[index])}`
          );
          history.priceFrom = updatedUnit.basePrice;
          history.priceTo = changedPrice[index];
        }
        if (!leasing && status[index] !== filteredSelectedUnits[index].status) {
          historyType.push('Status');
          historyDescription.push(`Status has been changed from ${updatedUnit.status} to ${status[index]}`);
        }
        if (leasing && status[index] !== filteredSelectedUnits[index].leaseStatus) {
          historyType.push('Status');
          historyDescription.push(`Status has been changed from ${updatedUnit.leaseStatus} to ${status[index]}`);
        }

        if (
          changedAllocation[index].tempAllocation !== filteredSelectedUnits[index].tempAllocation &&
          !changedAllocation[index].allocation
        ) {
          historyType.push('Temporary Allocation');
          historyDescription.push(
            `Temporary Allocation has been changed from ${
              filteredSelectedUnits[index].tempAllocation ? filteredSelectedUnits[index].tempAllocation : 'None'
            } to ${changedAllocation[index].tempAllocation ? changedAllocation[index].tempAllocation : 'None'}`
          );
          allocatedDate = new Date();
        }
        if (changedModel[index] !== filteredSelectedUnits[index].modelType) {
          historyType.push('Model');
          historyDescription.push(`Model Type has been changed from ${changedModel[index]} to ${updatedUnit.modelType}`);
        }
        if (rentalChange[index] !== filteredSelectedUnits[index].rental && filteredSelectedUnits[index].rental !== null) {
          historyType.push('Rental');
          historyDescription.push(
            `Rental has been changed from ${numToCurrency.format(updatedUnit.rental)} to ${numToCurrency.format(rentalChange[index])}`
          );
        }
        if (changedUnitType[index] !== filteredSelectedUnits[index].unitType) {
          historyType.push('Unit Type');
          historyDescription.push(`Unit Type has been changed from ${changedUnitType[index]} to ${updatedUnit.unitType}`);
        }

        if (changedAllocation[index].allocation !== filteredSelectedUnits[index].allocation) {
          historyType.push('Allocation');
          historyDescription.push(
            `Allocation has been changed from ${
              filteredSelectedUnits[index].allocation ? filteredSelectedUnits[index].allocation.fullName : 'None'
            } to ${changedAllocation[index].allocation ? changedAllocation[index].allocation.fullName : 'None'}`
          );
          if (selectedUnits[index].allocation && filteredSelectedUnits[index].allocation) {
            removeAllocations = true;
          }
          allocatedDate = new Date();
        }

        history.type = historyType.join(', ');
        history.description = historyDescription.join(', ');

        let updateUnitObject: any = {
          _id: updatedUnit._id,
          allocatedDate: allocatedDate,
          allocation:
            changedAllocation && changedAllocation[index] && changedAllocation[index].allocation
              ? changedAllocation[index].allocation._id
              : null,
          tempAllocation: changedAllocation && changedAllocation[index] ? changedAllocation[index].tempAllocation : null,
          basePrice: changedPrice[index],
          history: history,
          modelType: updatedUnit.modelType,
          unitType: updatedUnit.unitType,
          size: parseFloat(updatedUnit.size),
          exposure: updatedUnit.exposure,
          bathroom: parseFloat(updatedUnit.bathroom),
          outdoor: updatedUnit.outdoor,
          removeAllocations: removeAllocations,
        };

        if (leasing) {
          updateUnitObject.leaseStatus = status[index];
          updateUnitObject.status = filteredSelectedUnits[index].status;
        } else {
          updateUnitObject.status = status[index];
        }

        if (updatedUnit.rental !== null) {
          updateUnitObject.rental = parseFloat(rentalChange[index]);
        }

        return updateUnitObject;
      });

      updateUnits({ variables: { unitArray: updatedUnits, project: project._id } });
    }
  };

  const handleAllText = (event: any) => {
    let allUnits = filteredSelectedUnits.map((unit: IUnit) => {
      return {
        ...unit,
        [event.target.name]: event.target.value,
      };
    });
    if (event.target.name === 'modelType') {
      setAllModels(event.target.value);
    } else setAllUnitTypes(event.target.value);
    setFilteredSelectedUnits(allUnits);
  };

  const handleAllAmountInput = (event: any, type: string) => {
    let newAmountChange: any;
    let newPercent: any;
    let newPrice: any;
    if (type === 'same') {
      if (project.netHst) {
        // The typed amount is a net-of-HST increase — bump each unit's net price by it, then
        // convert back to gross (still what gets saved as unit.basePrice).
        newPrice = changedPrice.map((amount: any, index: number) => {
          const newNet = netHST(filteredSelectedUnits[index].basePrice, project) + parseFloat(event.target.value);
          return Math.round(newNet * 1.13);
        });
        newAmountChange = amountChange.map((amount: any, index: number) => newPrice[index] - filteredSelectedUnits[index].basePrice);
        newPercent = percentages.map(
          (amount: any, index: number) =>
            Math.round(((newPrice[index] - filteredSelectedUnits[index].basePrice) / filteredSelectedUnits[index].basePrice) * 100000) /
            1000,
        );
      } else {
        newAmountChange = amountChange.map((amount: any) => {
          return event.target.value;
        });
        newPercent = percentages.map((amount: any, index: number) => {
          return Math.round((event.target.value / filteredSelectedUnits[index].basePrice) * 100000) / 1000;
        });
        newPrice = changedPrice.map((amount: any, index: number) => {
          return filteredSelectedUnits[index].basePrice + parseInt(event.target.value);
        });
      }
      setAllAmount(event.target.value);
    } else if (type === 'level') {
      let lowestLevel = parseInt(filteredSelectedUnits[0].level);
      newAmountChange = amountChange.map((amount: any, index: number) => {
        return event.target.value * (parseInt(filteredSelectedUnits[index].level, 10) - lowestLevel + 1);
      });
      newPercent = percentages.map((amount: any, index: number) => {
        return (
          Math.round(
            ((event.target.value * (parseInt(filteredSelectedUnits[index].level, 10) - lowestLevel + 1)) /
              filteredSelectedUnits[index].basePrice) *
              100000
          ) / 1000
        );
      });
      newPrice = changedPrice.map((amount: any, index: number) => {
        return (
          filteredSelectedUnits[index].basePrice +
          parseInt(event.target.value) * (parseInt(filteredSelectedUnits[index].level, 10) - lowestLevel + 1)
        );
      });
      if (allSamePrice !== '0') {
        newPrice = changedPrice.map((amount: any, index: number) => {
          return (
            parseInt(allSamePrice, 10) + parseInt(event.target.value) * (parseInt(filteredSelectedUnits[index].level, 10) - lowestLevel + 1)
          );
        });
      }
      setAllFloorPremiums(event.target.value);
    } else if (type === 'samePrice') {
      newAmountChange = amountChange.map((amount: any) => {
        return parseInt(event.target.value);
      });
      newPercent = percentages.map((amount: any, index: number) => {
        return Math.round((event.target.value / filteredSelectedUnits[index].basePrice) * 100000) / 1000;
      });
      newPrice = changedPrice.map((amount: any, index: number) => {
        return parseInt(event.target.value);
      });
      setAllSamePrice(event.target.value);
    }
    setAmountChange(newAmountChange);
    setPercentages(newPercent);
    setChangedPrice(newPrice);
  };

  const handleAllSqFt = (event: any) => {
    if (event.target.value) {
      let newPrice: any;
      let newAmount: any;
      let newPercent: any;
      if (project.netHst) {
        // The typed $/sqft is a net-of-HST rate — size * rate gives the net price, then convert
        // back to gross (still what's saved as unit.basePrice).
        newPrice = changedPrice.map((amount: any, index: number) => {
          const newNet = filteredSelectedUnits[index].size * parseFloat(event.target.value);
          return Math.round(newNet * 1.13);
        });
        newAmount = amountChange.map((amount: any, index: number) => newPrice[index] - filteredSelectedUnits[index].basePrice);
        newPercent = percentages.map(
          (amount: any, index: number) =>
            Math.round(((newPrice[index] - filteredSelectedUnits[index].basePrice) / filteredSelectedUnits[index].basePrice) * 100000) /
            1000,
        );
      } else {
        newAmount = amountChange.map((amount: any, index: number) => {
          return Math.round(filteredSelectedUnits[index].size * parseInt(event.target.value) - filteredSelectedUnits[index].basePrice);
        });
        newPrice = changedPrice.map((amount: any, index: number) => {
          return Math.round(filteredSelectedUnits[index].size * parseInt(event.target.value));
        });
        newPercent = percentages.map((amount: any, index: number) => {
          return Math.round(
            ((filteredSelectedUnits[index].size * parseInt(event.target.value) - filteredSelectedUnits[index].basePrice) /
              filteredSelectedUnits[index].basePrice) *
              100
          );
        });
      }
      setChangedPrice(newPrice);
      setAmountChange(newAmount);
      setPercentages(newPercent);
    } else {
      const newPrice = changedPrice.map((amount: any, index: number) => filteredSelectedUnits[index].basePrice);
      setChangedPrice(newPrice);
      setAmountChange(amountChange.map((amount: any) => 0));
      setPercentages(percentages.map((amount: any) => 0));
    }
    setAllSqFt(event.target.value);
  };

  const handleAllRental = (event: any, type: string) => {
    if (event) {
      if (type === 'sqft') {
        const newRental = rentalChange.map((rental: any, index: number) => {
          return Math.round(filteredSelectedUnits[index].size * parseFloat(event.target.value));
        });
        setRentalChange(newRental);
        setAllRental(event.target.value);
      } else if (type === 'price') {
        const newRental = rentalChange.map((rental: any, index: number) => {
          return parseFloat(event.target.value);
        });
        setRentalChange(newRental);
        setAllRentalSamePrice(event.target.value);
      }
    }
  };

  const handleAllPercent = (event: any) => {
    let newPercent: any;
    let newAmount: any;
    let newPrice: any;
    if (project.netHst) {
      // The typed percentage increases each unit's net-of-HST price, not the gross price directly.
      newPrice = changedPrice.map((amount: any, index: number) => {
        const originalNet = netHST(filteredSelectedUnits[index].basePrice, project);
        const newNet = originalNet * (1 + parseFloat(event.target.value) / 100);
        return Math.round(newNet * 1.13);
      });
      newAmount = amountChange.map((amount: any, index: number) => newPrice[index] - filteredSelectedUnits[index].basePrice);
      newPercent = percentages.map(
        (amount: any, index: number) =>
          Math.round(((newPrice[index] - filteredSelectedUnits[index].basePrice) / filteredSelectedUnits[index].basePrice) * 100000) /
          1000,
      );
    } else {
      newPercent = percentages.map((amount: any) => {
        return event.target.value;
      });
      newAmount = amountChange.map((amount: any, index: number) => {
        return Math.round((event.target.value * filteredSelectedUnits[index].basePrice) / 100);
      });
      newPrice = changedPrice.map((amount: any, index: number) => {
        return filteredSelectedUnits[index].basePrice + Math.round((event.target.value * filteredSelectedUnits[index].basePrice) / 100);
      });
    }
    setChangedPrice(newPrice);
    setAmountChange(newAmount);
    setPercentages(newPercent);
    setAllPercent(event.target.value);
  };

  const handleAllStatus = (event: any) => {
    const allStatus = status.map((amount: any) => {
      return event.target.value;
    });
    setNewStatus(event.target.value);
    setStatus(allStatus);
  };

  const handleAllRound = (event: any) => {
    let newPrice = changedPrice;
    if (project.netHst) {
      // Round the net-of-HST price, then convert back to gross (still what's saved as
      // unit.basePrice) — otherwise "round to $999" etc. would round the gross price instead.
      const roundNet = (net: number) => {
        if (event.target.value === 'thousandsDown') return Math.floor(net / 1000) * 1000;
        if (event.target.value === 'thousandsUp') return Math.ceil(net / 1000) * 1000;
        if (event.target.value === 'hundredsDown') return Math.floor(net / 100) * 100;
        if (event.target.value === 'hundredsUp') return Math.ceil(net / 100) * 100;
        if (event.target.value === '999') return Math.floor(net / 1000) * 1000 + 999;
        return net;
      };
      if (event.target.value === '990') {
        newPrice = changedPrice.map((amount: any, index: number) => {
          const originalNet = netHST(filteredSelectedUnits[index].basePrice, project);
          const roundedNet = Math.floor(originalNet / 1000) * 1000 + 990;
          return Math.round(roundedNet * 1.13);
        });
      } else {
        newPrice = changedPrice.map((amount: any) => {
          const roundedNet = roundNet(netHST(amount, project));
          return Math.round(roundedNet * 1.13);
        });
      }
    } else if (event.target.value === 'thousandsDown') {
      newPrice = changedPrice.map((amount: any, index: number) => {
        return Math.floor(amount / 1000) * 1000;
      });
    } else if (event.target.value === 'thousandsUp') {
      newPrice = changedPrice.map((amount: any, index: number) => {
        return Math.ceil(amount / 1000) * 1000;
      });
    } else if (event.target.value === 'hundredsDown') {
      newPrice = changedPrice.map((amount: any, index: number) => {
        return Math.floor(amount / 100) * 100;
      });
    } else if (event.target.value === 'hundredsUp') {
      newPrice = changedPrice.map((amount: any, index: number) => {
        return Math.ceil(amount / 100) * 100;
      });
    } else if (event.target.value === '999') {
      newPrice = changedPrice.map((amount: any, index: number) => {
        return Math.floor(amount / 1000) * 1000 + 999;
      });
    } else if (event.target.value === '990') {
      newPrice = changedPrice.map((amount: any, index: number) => {
        return Math.floor(filteredSelectedUnits[index].basePrice / 1000) * 1000 + 990;
      });
    }
    setChangedPrice(newPrice);
  };

  const handleAmountInput = (event: any, index: number, price: number) => {
    const newAmountChange = amountChange.map((amount: any, i: number) => {
      if (index === i) {
        return event.target.value;
      } else {
        return amount;
      }
    });
    const newPercent = percentages.map((amount: any, i: number) => {
      if (index === i) {
        return Math.round((event.target.value / price) * 100000) / 1000;
      } else {
        return amount;
      }
    });
    const newPrice = changedPrice.map((amount: any, i: number) => {
      if (index === i) {
        return price + parseInt(event.target.value);
      } else {
        return amount;
      }
    });
    setAmountChange(newAmountChange);
    setPercentages(newPercent);
    setChangedPrice(newPrice);
  };

  const handleNewPrice = (event: any, index: number, price: number) => {
    let onlyNumber = new RegExp('^\\d+$');
    let validNumber = onlyNumber.test(event.target.value);
    if (validNumber) {
      const newAmountChange = amountChange.map((amount: any, i: number) => {
        if (index === i) {
          return event.target.value - price;
        } else {
          return amount;
        }
      });
      const newPercent = percentages.map((amount: any, i: number) => {
        if (index === i) {
          return Math.round(((event.target.value - price) / price) * 100000) / 1000;
        } else {
          return amount;
        }
      });
      const newPrice = changedPrice.map((amount: any, i: number) => {
        if (index === i) {
          return parseInt(event.target.value);
        } else {
          return amount;
        }
      });
      setAmountChange(newAmountChange);
      setPercentages(newPercent);
      setChangedPrice(newPrice);
    }
  };

  // The value entered here is net-of-HST, so it gets converted to a gross price before being
  // written to changedPrice (still the value saved as unit.basePrice on submit — Net Price itself
  // is never saved). A project.netHst project's netHST is a flat /1.13, so its inverse is *1.13;
  // every other project's netHST branches by price bracket, so grossPriceFromNetHST inverts that instead.
  const handleNewNetPrice = (event: any, index: number, price: number) => {
    let onlyNumber = new RegExp('^\\d+$');
    let validNumber = onlyNumber.test(event.target.value);
    if (validNumber) {
      const grossPrice =
        project.netHst
          ? Math.round(parseFloat(event.target.value) * 1.13)
          : Math.round(grossPriceFromNetHST(parseFloat(event.target.value)));
      const newAmountChange = amountChange.map((amount: any, i: number) => {
        if (index === i) {
          return grossPrice - price;
        } else {
          return amount;
        }
      });
      const newPercent = percentages.map((amount: any, i: number) => {
        if (index === i) {
          return Math.round(((grossPrice - price) / price) * 100000) / 1000;
        } else {
          return amount;
        }
      });
      const newPrice = changedPrice.map((amount: any, i: number) => {
        if (index === i) {
          return grossPrice;
        } else {
          return amount;
        }
      });
      setAmountChange(newAmountChange);
      setPercentages(newPercent);
      setChangedPrice(newPrice);
    }
  };

  const handlePercent = (event: any, index: number, price: number) => {
    let onlyNumber = new RegExp('^\\d+$');
    let validNumber = onlyNumber.test(event.target.value);
    if (validNumber) {
      const newPercent = percentages.map((amount: any, i: number) => {
        if (index === i) {
          return event.target.value;
        } else {
          return amount;
        }
      });
      const newAmount = amountChange.map((amount: any, i: number) => {
        if (index === i) {
          return Math.round((event.target.value * price) / 100);
        } else {
          return amount;
        }
      });
      const newPrice = changedPrice.map((amount: any, i: number) => {
        if (index === i) {
          return price + Math.round((event.target.value * price) / 100);
        } else {
          return amount;
        }
      });
      setChangedPrice(newPrice);
      setAmountChange(newAmount);
      setPercentages(newPercent);
    }
  };

  const handleRentalInput = (event: any, index: number, price: number) => {
    let onlyNumber = new RegExp('^\\d+$');
    let validNumber = onlyNumber.test(event.target.value);
    if (validNumber) {
      const newRentalChange = rentalChange.map((amount: any, i: number) => {
        if (index === i) {
          return event.target.value;
        } else {
          return amount;
        }
      });
      setRentalChange(newRentalChange);
    }
  };

  const activeDisplayFilter = !displayType.some((code: string) => salesStatusCodes.includes(code))
    ? 'notSold'
    : !displayType.includes('C') && displayType.some((code: string) => salesStatusCodes.includes(code))
    ? 'firm'
    : displayType.includes('C') && !displayType.some((code: string) => salesStatusCodes.includes(code) && code !== 'C')
    ? 'conditional'
    : null;

  return unitLoading ? (
    <Box sx={{ position: 'relative' }}>
      <LoadingWrapper title="Units are being updated..." modal={false} />
    </Box>
  ) : (
    <Box sx={{ margin: '0 auto', backgroundColor: '#f4f4f2', p: 2.5, borderRadius: '10px' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Typography sx={{ fontSize: '1.3rem', fontWeight: 700, color: '#1a1a1a' }}>Edit Selected Units</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          {!leasing ? (
            <>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={activeDisplayFilter}
                onChange={(_, newValue) => newValue && handleDisplayType(newValue)}
                sx={{
                  backgroundColor: '#fff',
                  '& .MuiToggleButton-root': { textTransform: 'none', px: 2 },
                  '& .Mui-selected': { backgroundColor: `${DARK_GREEN} !important`, color: '#fff !important' },
                }}
              >
                <ToggleButton value="notSold">Not Sold</ToggleButton>
                <ToggleButton value="firm">Firm</ToggleButton>
                <ToggleButton value="conditional">Conditional</ToggleButton>
              </ToggleButtonGroup>
            </>
          ) : null}
          <Button
            form="submitForm"
            variant="contained"
            sx={{ textTransform: 'none', backgroundColor: DARK_GREEN, fontWeight: 700, '&:hover': { backgroundColor: '#233530' } }}
            onClick={() => setOpenConfirm(true)}
          >
            Update Units
          </Button>
        </Box>
      </Box>

      {/* Bulk price adjustments */}
      <Section title="Bulk Price Adjustments" dense rowSpacing={3}>
        <Grid item md={3} sm={4} xs={6}>
          <TextField
            size="small"
            key={'allUnitsPercentage'}
            fullWidth
            type="number"
            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
            label={'Increase All Units (Percentage)'}
            title={'Increase All Units (Percentage)'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAllPercent(e);
            }}
            value={allPercent}
            InputProps={{
              startAdornment: <InputAdornment position="start">%</InputAdornment>,
            }}
            placeholder="Enter Percentage"
            required={true}
            autoFocus={false}
          />
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <TextField
            size="small"
            fullWidth
            key={'allUnitsAmount'}
            type="number"
            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
            label={'Increase All Units'}
            title={'Increase All Units'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAllAmountInput(e, 'same');
            }}
            value={allAmount}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            placeholder="Enter Amount"
            required={true}
          />
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <TextField
            size="small"
            key={'allUnitsSqft'}
            fullWidth
            type="number"
            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
            label={'Set All By SQFT'}
            title={'Set All By SQFT'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAllSqFt(e);
            }}
            value={allSqFt}
            InputProps={{
              startAdornment: <InputAdornment position="start"></InputAdornment>,
            }}
            placeholder="Enter Amount"
            required={true}
          />
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <FormControl size="small" sx={{ width: '100%' }}>
            <InputLabel id="demo-simple-select-label">Round All Prices</InputLabel>
            <Select
              size="small"
              label="Round All Prices"
              sx={{ width: '100%' }}
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={newStatus}
              onChange={(e: any) => {
                handleAllRound(e);
              }}
            >
              <MenuItem value={'thousandsDown'}>Thousands (Down)</MenuItem>
              <MenuItem value={'thousandsUp'}>Thousands (Up)</MenuItem>
              <MenuItem value={'hundredsDown'}>Hundreds (Down)</MenuItem>
              <MenuItem value={'hundredsUp'}>Hundreds (Up)</MenuItem>
              <MenuItem value={'990'}>End with $990</MenuItem>
              <MenuItem value={'999'}>End with $999</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {checkSameModel ? (
          <Grid item md={3} sm={4} xs={6}>
            <TextField
              size="small"
              key={'allUnitsFloorPrems'}
              fullWidth
              type="number"
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              label={'Increase Amount by Floor Premiums'}
              title={'Increase Amount by Floor Premiums'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleAllAmountInput(e, 'level');
              }}
              value={allFloorPremiums}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              placeholder="Enter Amount"
              required={true}
            />
          </Grid>
        ) : null}
        {checkSameModel ? (
          <Grid item md={3} sm={4} xs={6}>
            <TextField
              size="small"
              key={'allUnitsModels'}
              fullWidth
              type="number"
              onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
              label={'Change all Models to Same Price'}
              title={'Change all Models to Same Price'}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleAllAmountInput(e, 'samePrice');
              }}
              value={allSamePrice}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              placeholder="Enter Amount"
              required={true}
            />
          </Grid>
        ) : null}
        <Grid item md={3} sm={4} xs={6}>
          <TextField
            size="small"
            key={'rentalsqft'}
            fullWidth
            type="number"
            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
            label={'Set Rental By SQFT'}
            title={'Set Rental By SQFT'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAllRental(e, 'sqft');
            }}
            value={allRental}
            placeholder="Enter Amount"
            required={true}
          />
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <TextField
            size="small"
            key={'rentalPrice'}
            fullWidth
            type="number"
            onWheel={(e) => e.target instanceof HTMLElement && e.target.blur()}
            label={'Set Rental to Same Price'}
            title={'Set Rental to Same Price'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAllRental(e, 'price');
            }}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            value={allRentalSamePrice}
            placeholder="Enter Amount"
            required={true}
          />
        </Grid>
      </Section>

      {/* Other bulk changes */}
      <Section title="Allocation, Status & Type" dense>
        <Grid item md={3} sm={4} xs={6}>
          <Autocomplete
            sx={{
              width: '100%',
              '& .MuiFormLabel-asterisk': {
                color: 'red',
              },
            }}
            id={'search'}
            disableClearable
            freeSolo={true}
            options={realtors.map((realtor: any) => realtor.fullName)}
            getOptionLabel={(option: any) => option.fullName || option}
            onBlur={() => (editableKeyToFocus.current = null)}
            openOnFocus={true}
            PopperComponent={CustomPopper}
            value={allAllocations}
            onChange={handleAllAllocations()}
            onInputChange={(e: any) => {
              handleAllAllocationsChange(e);
            }}
            renderInput={(params) => <TextField required={false} {...params} size="small" label={'Set All Allocations'} />}
          />
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <FormControl size="small" sx={{ width: '100%' }}>
            <InputLabel id="demo-simple-select-label">Change All Statuses</InputLabel>
            <Select
              size="small"
              label="Change All Statuses"
              sx={{ width: '100%' }}
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              value={newStatus}
              onChange={(e: any) => {
                handleAllStatus(e);
              }}
            >
              {allStatus.map((status, index) => {
                return (
                  <MenuItem key={index} value={status.code}>
                    {status.code}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <TextField
            size="small"
            key={'allModels'}
            fullWidth
            name="modelType"
            label={'Change All Selected Units By Model'}
            title={'Change All Selected Units By Model'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAllText(e);
            }}
            value={allModels}
            placeholder="Enter Text"
            required={true}
          />
        </Grid>
        <Grid item md={3} sm={4} xs={6}>
          <TextField
            size="small"
            key={'allUnitTypes'}
            fullWidth
            name="unitType"
            label={'Change All Selected Units By Unit Types'}
            title={'Change All Selected Units By Unit Types'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAllText(e);
            }}
            value={allUnitTypes}
            placeholder="Enter Text"
            required={true}
          />
        </Grid>
      </Section>

      {/* Summary */}
      {filteredSelectedUnits.length && !leasing ? (
        <Section title="Summary" dense>
          <StatTile label="Count" value={filteredSelectedUnits.length} />
          <StatTile
            label="Total Original Price"
            value={numToCurrency.format(filteredSelectedUnits.reduce((a: any, b: any) => a + b.basePrice, 0))}
          />
          <StatTile label="Total New Price" value={numToCurrency.format(changedPrice.reduce((a: any, b: any) => a + b, 0))} />
          <StatTile
            label="Percentage Changed"
            value={`${(
              ((changedPrice.reduce((a: any, b: any) => a + b, 0) -
                filteredSelectedUnits.reduce((a: any, b: any) => a + b.basePrice, 0)) /
                filteredSelectedUnits.reduce((a: any, b: any) => a + b.basePrice, 0)) *
              100
            ).toFixed(2)}%`}
          />
          <StatTile
            label="Total Difference"
            value={numToCurrency.format(
              changedPrice.reduce((a: any, b: any) => a + b, 0) - filteredSelectedUnits.reduce((a: any, b: any) => a + b.basePrice, 0)
            )}
          />
          <StatTile
            label="Average Original PPSF"
            value={numToCurrency.format(
              filteredSelectedUnits.reduce((a: any, b: any) => a + b.basePrice, 0) /
                filteredSelectedUnits.reduce((a: any, b: any) => a + b.size, 0)
            )}
          />
          <StatTile
            label="Average New PPSF"
            value={numToCurrency.format(
              changedPrice.reduce((a: any, b: any) => a + b, 0) / filteredSelectedUnits.reduce((a: any, b: any) => a + b.size, 0)
            )}
          />
          <StatTile
            label="Average Size"
            value={numberWithCommas(filteredSelectedUnits.reduce((a: any, b: any) => a + b.size, 0) / filteredSelectedUnits.length)}
          />
        </Section>
      ) : null}

      {/* Units table */}
      <form id="submitForm" onSubmit={submitChanges}>
        <Box sx={{ backgroundColor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden', display: 'flex' }}>
          <VirtualizedTable columns={unitColumns} data={filteredSelectedUnits} itemHeight={100} tableHeight={450} />
        </Box>
        <Dialog
          open={openConfirm}
          onClose={() => {
            setOpenConfirm(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">{'Confirm Changes?'}</DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">Confirm the changes for the selected units?</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setOpenConfirm(false);
              }}
              color="primary"
            >
              Cancel
            </Button>
            <Button type="submit" form="submitForm" color="primary" autoFocus>
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </form>
    </Box>
  );
};

const GETREALTORS = gql`
  query userMany($filter: FilterFindManyUserInput) {
    userMany(filter: $filter, limit: 10000) {
      _id
      firstName
      lastName
      fullName
      realtor {
        _id
      }
      locked
    }
  }
`;

const UPDATEUNIT = gql`
  mutation unitUpdateManyInfo($unitArray: [NewUnitInput!], $project: MongoID!) {
    unitUpdateManyInfo(unitArray: $unitArray, project: $project) {
      _id
      allocation {
        fullName
        _id
      }
      allocatedDate
      tempAllocation
      basePrice
      status
      leaseStatus
      modelType
      unitType
      size
      exposure
      bathroom
      outdoor {
        name
        size
      }
      worksheets {
        _id
      }
      rental
    }
  }
`;

const UPDATEPRICELIST = gql`
  mutation priceListUpdateById($_id: MongoID!, $record: UpdateByIdPriceListInput!) {
    priceListUpdateById(_id: $_id, record: $record) {
      record {
        _id
        name
        project {
          _id
        }
        draft {
          _id
          status
          suite
          unit
          unitId
          level
          modelType
          unitType
          basePrice
          size
          outdoor {
            name
            size
          }
          bathroom
          exposure
          col
          row
          stackCol
          stackRow
        }
      }
    }
  }
`;

export default EditSelected;
